import { PAEventTypeV2 } from '@dadohr/shared/domainTypes/productAnalytics';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  TextField,
  Typography
} from '@material-ui/core';
import { grey } from '@material-ui/core/colors';
import { Info, X } from 'lucide-react';
import React, { useCallback, useEffect, useState } from 'react';
import { DialogTitleWithTypography } from '../../../../../../components/DialogTitleWithTypography';
import { FormRow } from '../../../../../../components/Form/FormRow';
import { useTranslation } from '../../../../../../i18next';
import { addBreadcrumb, captureException } from '../../../../../../sentry';
import { callFirebaseFunction } from '../../../../../../services/firebaseFunctions';
import { useProductAnalytics } from '../../../../../../services/productAnalytics';

type AIGeneratorState = 'idle' | 'generating' | 'inserting' | 'error';

export enum MessageTone {
  Neutral = 'Neutral',
  Professional = 'Professional',
  Friendly = 'Friendly'
}

enum MessageLength {
  Short = 'Short',
  Medium = 'Medium',
  Long = 'Long'
}

interface GenerateMessageResponse {
  content: string;
  metadata: {
    tone: MessageTone;
    length?: MessageLength;
    generatedAt: number;
  };
}

interface AIContentGeneratorDialogProps {
  templateId?: string;
  templateName?: string;
  timelineItemId?: string;
  timelineItemType?: string;
  timelineItemTitle?: string;
  open: boolean;
  onClose: () => void;
  onGenerate: (prompt: string, tone: MessageTone) => Promise<void>;
  state: AIGeneratorState;
}

export const AIContentGeneratorDialog: React.FC<AIContentGeneratorDialogProps> = ({
  open,
  onClose,
  onGenerate,
  state,
  templateId,
  templateName,
  timelineItemId,
  timelineItemType,
  timelineItemTitle
}) => {
  const { t } = useTranslation();
  const [prompt, setPrompt] = useState('');
  const [tone, setTone] = useState<MessageTone>(MessageTone.Neutral);

  // Event tracking
  const { trackEvent } = useProductAnalytics();

  const TONE_OPTIONS: Array<{ label: string; value: MessageTone }> = [
    { label: 'Neutral', value: MessageTone.Neutral },
    { label: 'Professional', value: MessageTone.Professional },
    { label: 'Friendly', value: MessageTone.Friendly }
  ];

  // Reset prompt when dialog opens
  useEffect(() => {
    if (open) {
      setPrompt('');
    }
  }, [open]);

  const handleGenerate = useCallback(
    async (t: MessageTone) => {
      if (!prompt.trim()) return;
      await onGenerate(prompt, t);
    },
    [prompt, onGenerate]
  );

  const isGenerating = state === 'generating' || state === 'inserting';

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitleWithTypography>
        <div
          style={{
            background:
              'linear-gradient(0deg, #F4E9FF, #F4E9FF), linear-gradient(293.27deg, rgba(244, 233, 255, 0.3) -51.24%, rgba(244, 233, 255, 0.15) -15%, rgba(116, 169, 247, 0.15) 68.77%)',
            margin: '-32px -32px -16px',
            padding: '16px 24px',
            border: '0',
            borderBottom: '2px solid',
            borderImageSource:
              'linear-gradient(92.85deg, #6FAFFA -13.27%, #BB6BD9 81.7%)',
            borderImageSlice: 1
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              fontFamily: 'Roboto',
              fontWeight: 500,
              fontSize: '24px',
              color: '#3E2E4D'
            }}
          >
            <img
              src="/images/ai-modal-icon.svg"
              alt="AI"
              width={40}
              height={40}
            />
            <Typography component="h4" variant="h4">
              What would you like me to write?
            </Typography>
            <IconButton
              aria-label="close"
              onClick={onClose}
              size="small"
              style={{ color: grey[800], marginLeft: 'auto', marginRight: -10 }}
            >
              <X size={20} strokeWidth={1.5} />
            </IconButton>
          </div>
        </div>
      </DialogTitleWithTypography>
      <DialogContent
        style={{
          backgroundColor: '#F5F5F7',
          padding: '24px 24px 24px' // keep inner spacing, no extra gap
        }}
      >
        <FormRow>
          <TextField
            fullWidth
            multiline
            rows={4}
            variant="outlined"
            placeholder='Example: Write a welcome email to new employees whom we call "Acmers"'
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            disabled={isGenerating}
            autoFocus
            onKeyDown={(e) => {
              if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                handleGenerate(tone);
              }
            }}
          />
        </FormRow>
        <FormRow>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 12
            }}
          >
            <div
              style={{
                fontWeight: 500,
                whiteSpace: 'nowrap',
                flexShrink: 0,
                fontFamily: 'ebony'
              }}
            >
              Tone of voice:
            </div>

            <div
              style={{
                display: 'flex',
                border: '0.5px solid #D0D5DD',
                borderRadius: 3,
                overflow: 'hidden',
                height: 32,
                flexShrink: 0
              }}
            >
              {TONE_OPTIONS.map((opt, idx) => {
                const selected = tone === opt.value;
                return (
                  <button
                    key={opt.label}
                    type="button"
                    onClick={() => setTone(opt.value)}
                    style={{
                      fontFamily: 'ebony',
                      fontWeight: selected ? 500 : 350,
                      padding: '0 15px',
                      border: 'none',
                      background: selected ? '#0E78E4' : '#FFFFFF',
                      color: selected ? '#FFFFFF' : '#0B1220',
                      fontSize: 16,
                      cursor: 'pointer',
                      ...(idx > 0 ? { borderLeft: '1px solid #E5E7EB' } : {})
                    }}
                  >
                    <span
                      style={{
                        fontWeight: 500,
                        visibility: 'hidden',
                        display: 'block',
                        height: 0
                      }}
                    >
                      {opt.label}
                    </span>
                    {opt.label}
                  </button>
                );
              })}
            </div>
          </div>
        </FormRow>
      </DialogContent>
      <DialogActions
        style={{
          backgroundColor: '#F5F5F7',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginTop: '-10px',
          padding: '0 24px 24px'
        }}
      >
        <div
          style={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            color: '#475467'
          }}
        >
          <Info size={16} strokeWidth={2} />
          <span>AI-generated content should be reviewed for accuracy</span>
        </div>

        <Button
          color="primary"
          variant="contained"
          onClick={() => {
            trackEvent({
              eventType: PAEventTypeV2.AI_CONTENT_GENERATOR,
              name: 'AI Text Generation Requested',
              Explanation:
                'An admin clicked the button to submit their prompt and generate text',
              'Experience Template ID': templateId,
              'Experience Template Name': templateName,
              'Timeline Item ID': timelineItemId,
              'Timeline Item Type': timelineItemType,
              'Timeline Item Title': timelineItemTitle
            });
            onClose();
            handleGenerate(tone);
          }}
          disabled={!prompt.trim() || isGenerating}
        >
          {isGenerating ? 'Generating...' : 'Generate text'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

/**
 * Generates text using the Firebase callable function
 */
export const generateText = async (
  prompt: string,
  organizationId: string,
  tone: MessageTone
): Promise<string> => {
  const response = await callFirebaseFunction<GenerateMessageResponse>(
    'ai_generateMessage',
    {
      organization_id: organizationId,
      prompt,
      tone,
      length: MessageLength.Medium
    }
  );

  return response.content;
};

/**
 * Streams text into a callback function word by word
 * Creates the AI typing effect
 */
export const streamText = async (
  text: string,
  onChunk: (chunk: string) => void,
  onComplete: () => void,
  wordsPerChunk: number = 1,
  delayMs: number = 25
): Promise<void> => {
  const words = text.split(/(\s+)/); // Split by whitespace but keep the whitespace
  let currentText = '';

  for (let i = 0; i < words.length; i += wordsPerChunk) {
    const chunk = words.slice(i, i + wordsPerChunk).join('');
    currentText += chunk;
    onChunk(currentText);

    // Don't delay after the last chunk
    if (i + wordsPerChunk < words.length) {
      await new Promise((resolve) => setTimeout(resolve, delayMs));
    }
  }

  onComplete();
};

interface AIContentGeneratorContext {
  organizationId: string;
  templateId?: string;
  timelineItemId?: string;
  timelineItemTitle?: string;
  timelineItemType?: 'task' | 'message';
}

/**
 * Hook to manage AI content generation state
 */
export const useAIContentGenerator = (
  onInsertText: (text: string) => void,
  context: AIContentGeneratorContext
) => {
  const {
    organizationId,
    templateId,
    timelineItemId,
    timelineItemType,
    timelineItemTitle
  } = context;
  const [state, setState] = useState<AIGeneratorState>('idle');
  const [error, setError] = useState<string>();

  const generateAndInsert = useCallback(
    async (prompt: string, tone: MessageTone): Promise<boolean> => {
      try {
        setState('generating');
        setError(undefined);

        addBreadcrumb({
          category: 'ai.generation',
          message: 'Starting AI content generation',
          data: {
            tone,
            promptLength: prompt.length,
            organizationId,
            templateId,
            timelineItemId,
            timelineItemType
          }
        });

        const generatedText = await generateText(prompt, organizationId, tone);

        setState('inserting');

        // Stream the text word by word
        await streamText(
          generatedText,
          (chunk) => onInsertText(chunk),
          () => setState('idle')
        );

        return true;
      } catch (err) {
        const details = (err as { details?: { code?: string } })?.details;
        const errorCode = details?.code;

        console.error('AI content generation failed:', errorCode ?? err);

        captureException(err, {
          tags: {
            feature: 'ai_content_generator',
            errorCode: errorCode || 'unknown'
          },
          extra: {
            tone,
            promptLength: prompt.length,
            organizationId,
            templateId,
            timelineItemId,
            timelineItemType,
            timelineItemTitle
          }
        });

        setState('error');

        // Check for rate limit errors
        if (errorCode === 'AI_ERR_3002' || errorCode === 'AI_ERR_3003') {
          setError(
            'You have reached your usage limit. Please try again later.'
          );
        } else {
          setError(
            'This feature is currently unavailable. Please try again later.'
          );
        }

        return false;
      }
    },
    [onInsertText, organizationId, templateId, timelineItemId, timelineItemType]
  );

  return {
    state,
    error,
    generateAndInsert,
    resetState: () => {
      setState('idle');
      setError(undefined);
    }
  };
};
